import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button } from "semantic-ui-react";

const FeedbackCard = ({
  feedback,
  onEdit,
  onDelete,
  onMarkAsRead,
  canEdit,
  canDelete,
  isAllowedToEdit,
  isBeingMarkedAsRead,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getDisplayName = (fb) => {
    if (!fb.created_by) {
      const postId = fb.id || fb._id || "";
      const suffix = postId.replace(/[^0-9]/g, "").slice(-3) || "000";
      return `Anonymous #${suffix}`;
    }
    return fb.created_by;
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const postDate = new Date(date);
    const diffInSeconds = Math.floor((now - postDate) / 1000);
    if (diffInSeconds < 60) return "just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800)
      return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return postDate.toLocaleDateString();
  };

  const postId = feedback.id || feedback._id;
  const shouldTruncate = feedback.message.length > 200;
  const displayMessage =
    shouldTruncate && !isExpanded
      ? feedback.message.substring(0, 200) + "..."
      : feedback.message;
  const displayName = getDisplayName(feedback);

  return (
    <div
      style={{
        backgroundColor:
          feedback.is_read && isAllowedToEdit ? "#f8f9fa" : "#fff",
        borderRadius: "8px",
        padding: "1rem",
        marginBottom: "0.75rem",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        border:
          feedback.is_read && isAllowedToEdit
            ? "1px solid #28a745"
            : "1px solid #e0e0e0",
        opacity: feedback.is_read && isAllowedToEdit ? 0.7 : 1,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "0.5rem",
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginBottom: "0.25rem",
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontWeight: "bold", fontSize: "0.9rem" }}>
              {displayName}
            </div>
            {displayName.startsWith("Anonymous") && (
              <span
                style={{
                  fontSize: "0.7rem",
                  color: "#888",
                  backgroundColor: "#f0f0f0",
                  padding: "0.2rem 0.4rem",
                  borderRadius: "4px",
                }}
              >
                Anonymous
              </span>
            )}
            {feedback.is_read && isAllowedToEdit && (
              <span
                style={{
                  fontSize: "0.7rem",
                  color: "#28a745",
                  backgroundColor: "#d4edda",
                  padding: "0.2rem 0.4rem",
                  borderRadius: "4px",
                  fontWeight: "bold",
                }}
              >
                Read
              </span>
            )}
          </div>
          <div style={{ fontSize: "0.8rem", color: "#555" }}>
            {getTimeAgo(feedback.created_at || feedback.createdAt)}
          </div>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          {canEdit && (
            <Button
              size="mini"
              color="blue"
              onClick={() => onEdit(feedback)}
              style={{
                fontSize: "0.7rem",
                padding: "0.4rem 0.8rem",
              }}
            >
              Edit
            </Button>
          )}
          {isAllowedToEdit && !feedback.is_read && (
            <Button
              size="mini"
              color="green"
              loading={isBeingMarkedAsRead}
              disabled={isBeingMarkedAsRead}
              onClick={() => onMarkAsRead(postId)}
              style={{
                fontSize: "0.7rem",
                padding: "0.5rem 0.8rem",
              }}
            >
              {isBeingMarkedAsRead ? "Marking..." : "Mark as Read"}
            </Button>
          )}
          {canDelete && (
            <button
              onClick={() => onDelete(postId)}
              style={{
                background: "none",
                border: "none",
                color: "#FF4C4C",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "0.8rem",
                padding: "0.25rem 0.5rem",
              }}
            >
              Delete
            </button>
          )}
        </div>
      </div>
      <div
        style={{
          fontSize: "0.9rem",
          lineHeight: "1.4",
          whiteSpace: "pre-line", // ✅ Preserve line breaks
        }}
      >
        {displayMessage}
        {shouldTruncate && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              marginLeft: "0.5rem",
              color: "#666",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "0.8rem",
              textDecoration: "underline",
            }}
          >
            {isExpanded ? "Show less" : "Show more"}
          </button>
        )}
      </div>
    </div>
  );
};

FeedbackCard.propTypes = {
  feedback: PropTypes.shape({
    id: PropTypes.string,
    _id: PropTypes.string,
    message: PropTypes.string.isRequired,
    created_by: PropTypes.string,
    created_at: PropTypes.string,
    createdAt: PropTypes.string,
    is_read: PropTypes.bool,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onMarkAsRead: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
  canDelete: PropTypes.bool.isRequired,
  isAllowedToEdit: PropTypes.bool.isRequired,
  isBeingMarkedAsRead: PropTypes.bool.isRequired,
};

export default FeedbackCard;
